import { useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import { SEO } from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const CONTACT_EMAIL = "radar@aquivolabs.com";
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://radarapp.aquivolabs.com";

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ name, email, message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || data.error || "Something went wrong. Please try again.");
        return;
      }
      setSent(true);
      setName("");
      setEmail("");
      setMessage("");
    } catch {
      setError("Network error. Check your connection or email us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MarketingLayout>
      <SEO
        title="Contact"
        description="Contact Radar by Aquivo Labs — send a message or email radar@aquivolabs.com for sales and product questions."
        canonical="/contact"
      />
      <div className="pt-28 px-6 max-w-3xl mx-auto pb-24">
        <header className="mb-10 border-b border-white/[0.06] pb-8">
          <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Contact</p>
          <h1 className="text-3xl font-display font-bold tracking-tight text-foreground">Get in touch</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Send us a message below, or reach us directly at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-mono text-lime-400 hover:underline break-all">
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </header>

        {sent ? (
          <div
            className="rounded-xl border border-lime-500/20 bg-lime-500/5 px-4 py-3 text-sm text-foreground"
            role="status"
          >
            Thanks — your message was sent. We&apos;ll get back to you soon.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                name="name"
                type="text"
                autoComplete="name"
                required
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-background/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={320}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-message">Message</Label>
              <Textarea
                id="contact-message"
                name="message"
                required
                maxLength={8000}
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="bg-background/50 border-white/10 resize-y min-h-[140px]"
              />
            </div>
            {error ? (
              <p className="text-sm text-red-400" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? "Sending…" : "Send message"}
            </Button>
          </form>
        )}

        <nav className="mt-14 pt-8 border-t border-white/[0.06] text-sm text-muted-foreground">
          <Link to="/" className="text-lime-400 hover:underline">
            ← Back to home
          </Link>
        </nav>
      </div>
    </MarketingLayout>
  );
}
